import React from 'react'
import { Redirect, useHistory, useLocation } from 'react-router-dom'
import { PageLoader } from 'components/loader'
import { ROOT_PATH, LOGIN_PATH, LOGOUT_PATH } from 'routes/paths'
import { PrimaryHeader, Container } from '@arvoreeducacao/bonsai-ds'
import { getCookie } from 'utils/cookies'
import Navigation from 'components/client-management/navigation'
import FooterDesktop from 'components/footer/FooterDesktop'
import type { ServerError } from '@apollo/client'
import { useUserInfo } from 'utils/hooks/use-userInfo'
import useSetUserToThirdParty from 'utils/hooks/use-set-user-to-third-party'

type PrivateRouteProps = {
  withoutLayout?: boolean
  component: React.ComponentType
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({
  component: Component,
  withoutLayout
}) => {
  const history = useHistory()
  const location = useLocation()

  const { user, loading, error } = useUserInfo({
    fetchPolicy: 'network-only',
    errorPolicy: 'all'
  })

  useSetUserToThirdParty(user)

  if (Boolean(getCookie('access_token')) === false) {
    return (
      <Redirect
        to={{
          pathname: LOGIN_PATH,
          search: `redirectTo=${encodeURIComponent(location.pathname)}`
        }}
      />
    )
  }

  if (loading && !user.id) {
    return <PageLoader />
  }

  if (user.id) {
    if (!user.isAdmin) {
      return <Redirect to={ROOT_PATH} />
    }

    if (withoutLayout) {
      return <Component />
    }

    return (
      <div>
        <PrimaryHeader
          data-testid="primary-header"
          itemsDisabled={['comboDropdown', 'search']}
          theme={{ mode: 'combo' }}
          userMenuItens={[
            {
              text: 'Ir para Home',
              iconName: 'share',
              onClick: () => window.open(ROOT_PATH, '_blank')
            },
            {
              text: 'Sair',
              iconName: 'magazine',
              onClick: () => history.push(LOGOUT_PATH)
            }
          ]}
        />
        <Navigation />
        <Container data-testid="client-container">
          <Component />
        </Container>
        <FooterDesktop />
      </div>
    )
  }

  const hasNetworkError = error && error?.networkError ? true : false
  const status =
    hasNetworkError && (error?.networkError as ServerError)?.response
      ? (error?.networkError as ServerError)?.response?.status
      : null

  if (hasNetworkError && status === 500 && getCookie('access_token')) {
    return <Redirect to="/500" />
  }

  return (
    !loading && (
      <Redirect
        to={{
          pathname: LOGIN_PATH,
          search: `redirectTo=${encodeURIComponent(location.pathname)}`
        }}
      />
    )
  )
}

export default PrivateRoute
